import React, { useState, useEffect, useRef } from 'react';
import { QuotationRecord, AiRecommendationInsight } from '@/types/domain/quotation-comparison';
import { Button } from '@/components/ui/Button';
import { formatCurrency } from '@/lib/utils';
import { AlertTriangle, Trophy, X } from 'lucide-react';

interface QuotationSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmSelection: (justification: string) => Promise<void>;
  selectedQuotation: QuotationRecord;
  aiInsight?: AiRecommendationInsight | null;
  isSubmitting?: boolean;
}

export const QuotationSelectionModal: React.FC<QuotationSelectionModalProps> = ({
  isOpen,
  onClose,
  onConfirmSelection,
  selectedQuotation,
  aiInsight,
  isSubmitting = false,
}) => {
  const [justification, setJustification] = useState('');
  const [error, setError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const isAiRecommended = aiInsight?.recommendedQuotationId === selectedQuotation.id;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && !isSubmitting) onClose();
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      document.body.style.overflow = '';
      return undefined;
    }
  }, [isOpen, isSubmitting, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // If selecting non-recommended or rank > 1, enforce mandatory justification
    if (!isAiRecommended && !justification.trim()) {
      setError('Mandatory selection justification required when choosing a non-AI recommended supplier.');
      return;
    }

    if (justification.trim().length < 10) {
      setError('Please provide a meaningful justification (at least 10 characters).');
      return;
    }

    setError(null);
    await onConfirmSelection(justification.trim());
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 font-poppins">
      <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md animate-in fade-in" onClick={() => !isSubmitting && onClose()} />

      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        className="relative z-50 w-full max-w-lg rounded-2xl border border-border bg-surface dark:bg-[#0e101f] text-foreground p-6 shadow-2xl space-y-4 animate-in zoom-in-95"
      >
        <div className="flex items-start justify-between border-b border-border pb-3">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-2xl border bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
              <Trophy className="h-5 w-5 fill-current" />
            </div>

            <div>
              <h3 className="font-extrabold text-base text-foreground">Award Supplier & Confirm Selection</h3>
              <p className="text-xs text-foreground-secondary font-medium mt-0.5">
                {selectedQuotation.rfqNumber} • <strong className="text-foreground">{selectedQuotation.vendorName}</strong>
              </p>
            </div>
          </div>

          <Button
            variant="ghost"
            size="sm"
            disabled={isSubmitting}
            onClick={onClose}
            className="h-8 w-8 p-0 rounded-full text-foreground-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          {/* Selected Summary Box */}
          <div className="p-4 rounded-xl border border-primary/20 bg-primary/5 space-y-1">
            <span className="text-[10px] font-extrabold text-primary uppercase block">Winning Bid Overview</span>
            <div className="flex items-center justify-between">
              <span className="font-bold text-foreground text-sm">{selectedQuotation.vendorName}</span>
              <span className="font-mono text-base font-extrabold text-primary">
                {formatCurrency(selectedQuotation.evaluatedCost, selectedQuotation.currency)}
              </span>
            </div>
            <p className="text-foreground-secondary text-[11px]">
              Quotation Ref: <strong className="text-foreground font-mono">{selectedQuotation.quotationNumber}</strong> • Score:{' '}
              <strong className="text-purple-600 font-mono">{selectedQuotation.overallScore}/100</strong> (Rank #{selectedQuotation.rank || 1})
            </p>
          </div>

          {!isAiRecommended && (
            <div className="p-3 rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-900 space-y-1">
              <div className="flex items-center gap-1.5 font-bold">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <span>Non-Recommended Supplier Selection Warning</span>
              </div>
              <p className="text-[11px] text-amber-800 font-medium">
                You are selecting a supplier that differs from the AI Procurement Recommendation ({aiInsight?.recommendedVendorName}). A detailed justification reason is mandatory for audit logging.
              </p>
            </div>
          )}

          {error && (
            <div className="p-3 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-700 font-bold">
              {error}
            </div>
          )}

          <div className="space-y-1">
            <label className="font-bold text-foreground block">
              Selection Justification & Audit Reason {!isAiRecommended ? '*' : '(Recommended)'}
            </label>
            <textarea
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              rows={3}
              placeholder="Detail commercial, technical, warranty, or strategic reasons for selecting this supplier..."
              className="w-full rounded-xl border border-border bg-background p-3 text-xs font-medium text-foreground focus:ring-2 focus:ring-primary"
              required={!isAiRecommended}
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button variant="outline" type="button" disabled={isSubmitting} onClick={onClose} className="rounded-xl font-bold">
              Cancel
            </Button>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="rounded-xl font-bold text-white bg-emerald-600 hover:bg-emerald-700 shadow-2xs"
            >
              {isSubmitting ? 'Awarding Supplier...' : 'Confirm & Award Supplier →'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
